/**
 * Rock Off Retail — header search typewriter placeholder (item 3).
 *
 * Pure vanilla JS, no dependencies. Only ever writes to the input's
 * `placeholder` attribute — never `.value` — so it can never interfere with
 * anything the visitor actually types. Stops permanently (and restores the
 * theme's static base placeholder) the first time the input is focused or
 * receives an `input` event. Does nothing at all when the browser reports
 * (Reduced-motion gate removed 11 Oct 2026: a typed placeholder is text,
 * not motion. See connectedCallback.)
 *
 * BUGFIX (17 Aug Sunday sweep, item 5 — "animation disappears and doesn't
 * return"): the tick loop was a bare recursive `setTimeout` chain with no
 * awareness of tab visibility. Confirmed live (staff-preview session): once
 * `document.visibilityState` goes `hidden` (backgrounding the tab — a plain
 * alt-tab or switching to another tab, extremely normal user behaviour),
 * Chrome THROTTLES (not suspends — this distinction matters, see below)
 * background-tab timers, and the chain froze mid-phrase with `this.stopped`
 * still `false` — no error, no restart path on its own once the tab comes
 * back. Two related failure modes share the same missing piece: (1) alt-tab
 * away and back mid-animation leaves it stuck showing a half-typed phrase;
 * (2) navigating BACK to the page via bfcache (`pageshow` with
 * `event.persisted === true`) restores the whole element/timer state
 * exactly as it was frozen, with no fresh `connectedCallback` run to kick
 * it off again — "doesn't return" even on what looks like a reload. Fix: a
 * `visibilitychange` listener that unconditionally clears any in-flight
 * timer before deciding whether to reschedule (throttled, not suspended,
 * means a timer can still be technically pending when the tab becomes
 * visible again — clearing only on hide, as an earlier version of this fix
 * did, let a second chain stack on top of a still-live one on every
 * hide/show cycle), plus a `pageshow` listener that fully resets state
 * (including `mode`) on a `persisted` (bfcache) restore, and no
 * `{ once: true }` on the focus/input stop listeners (a bfcache restore can
 * re-arm the animation after the visitor already triggered the one-shot
 * stop once, permanently orphaning it — `stop()` is idempotent, so a
 * plain, always-attached listener is what the "stops on first focus"
 * contract actually needs across a restart). A genuine hard refresh already
 * worked fine (connectedCallback runs fresh); this only needed to cover the
 * paths where the DOM/JS state survives instead of resetting.
 */
class RockoffSearchTypewriter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    const phrasesEl = this.querySelector('script[data-phrases]');
    if (!this.input || !phrasesEl) return;

    let phrases = [];
    try {
      phrases = JSON.parse(phrasesEl.textContent);
    } catch (error) {
      phrases = [];
    }
    phrases = Array.isArray(phrases) ? phrases.filter((phrase) => typeof phrase === 'string' && phrase.trim() !== '') : [];
    if (phrases.length === 0) return;

    /* 11 Oct 2026 (Sohail): the reduced-motion gate is gone. A placeholder
       typed one letter at a time is text, not motion: nothing moves, scales
       or flashes, and it stops the moment the box is used. With Windows
       "Animation effects" off, Chrome reports prefers-reduced-motion and the
       box sat blank for Sohail, which read as broken rather than calm. */

    this.phrases = phrases;
    // Codex 28 Oct: capture the base text ONCE. A reconnect mid-animation would
    // otherwise take the half-typed phrase as the text to restore on stop.
    if (this.basePlaceholder === undefined) this.basePlaceholder = this.input.getAttribute('placeholder') || '';
    // 28 Oct 2026: USF's instant search (vendor assets/usf-boot.js) CLONES the
    // search input and swaps the copy in (cloneNode + replaceChild) to attach its
    // own handlers, at a time that depends on load order. The old code kept the
    // input it found first, so after the swap it animated a detached box and the
    // visible one showed the static text: "we keep losing the animated text".
    // Always write to the input that is in this element NOW.
    this.liveInput = () => {
      const current = this.querySelector('input[type="search"]');
      if (current) this.input = current;
      return this.input;
    };
    this.stopped = false;
    this.phraseIndex = 0;
    this.charIndex = 0;
    this.mode = 'type'; // 'type' | 'pause-after-type' | 'delete' | 'pause-after-delete'

    const stop = () => {
      if (this.stopped) return;
      this.stopped = true;
      clearTimeout(this.timer);
      this.liveInput().setAttribute('placeholder', this.basePlaceholder);
    };
    // BUGFIX (fresh-checker review): NOT `{ once: true }`. A bfcache restore
    // (see onPageShow below) can re-arm the animation after the visitor has
    // already focused/typed once, and `stop` is idempotent (`if (this.
    // stopped) return`) — a plain, always-attached listener is what actually
    // keeps the "stops the first time the input is focused" contract true
    // across a restart, instead of silently losing its only trigger the
    // first time it fires.
    // 28 Oct 2026: listen on this element, not on the input, so a box that USF
    // swapped in still stops the animation (focusin and input both bubble).
    if (this.stopHandler) {
      this.removeEventListener('focusin', this.stopHandler);
      this.removeEventListener('input', this.stopHandler);
    }
    this.addEventListener('focusin', stop);
    this.addEventListener('input', stop);
    this.stopHandler = stop;
    this.stop = stop;

    this.TYPE_MS = 65;
    this.DELETE_MS = 32;
    this.PAUSE_AFTER_TYPE_MS = 1400;
    this.PAUSE_AFTER_DELETE_MS = 400;

    this.typeTick = () => {
      if (this.stopped) return;
      const phrase = this.phrases[this.phraseIndex % this.phrases.length];
      this.mode = 'type';

      if (this.charIndex <= phrase.length) {
        this.liveInput().setAttribute('placeholder', phrase.slice(0, this.charIndex));
        this.charIndex += 1;
        this.timer = setTimeout(this.typeTick, this.TYPE_MS);
        return;
      }

      this.mode = 'pause-after-type';
      this.timer = setTimeout(() => this.deleteTick(phrase), this.PAUSE_AFTER_TYPE_MS);
    };

    this.deleteTick = (phrase) => {
      if (this.stopped) return;
      this.mode = 'delete';
      if (this.charIndex > 0) {
        this.charIndex -= 1;
        this.liveInput().setAttribute('placeholder', phrase.slice(0, this.charIndex));
        this.timer = setTimeout(() => this.deleteTick(phrase), this.DELETE_MS);
        return;
      }
      this.phraseIndex += 1;
      this.mode = 'pause-after-delete';
      this.timer = setTimeout(() => {
        this.charIndex = 0;
        this.typeTick();
      }, this.PAUSE_AFTER_DELETE_MS);
    };

    // Visibility guard: a backgrounded tab throttles/suspends setTimeout,
    // which can silently freeze the recursive tick chain forever (confirmed
    // live). Clear the pending timer the instant the tab hides so nothing
    // accumulates, and cleanly resume from the current phrase/char position
    // the instant it's visible again — never leaves the placeholder stuck
    // mid-phrase.
    this.onVisibilityChange = () => {
      if (this.stopped) return;
      // BUGFIX (fresh-checker review): clear FIRST, unconditionally, before
      // checking the new visibility state. Chrome throttles background-tab
      // timers, it doesn't reliably suspend them — the original version
      // only cleared `this.timer` on the hide branch, so a timer that was
      // still technically pending (just heavily delayed) when the tab came
      // back visible was never cancelled before the show branch scheduled a
      // SECOND one, and every subsequent hide/show cycle added another
      // chain on top. Clearing unconditionally here guarantees at most one
      // in-flight timer for this element at any time, regardless of how the
      // visibility events interleave with an already-throttled chain.
      clearTimeout(this.timer);
      if (document.visibilityState === 'hidden') {
        return;
      }
      // Visible again: resume from wherever the animation was, without
      // restarting the phrase from scratch.
      if (this.mode === 'delete' || this.mode === 'pause-after-type') {
        const phrase = this.phrases[this.phraseIndex % this.phrases.length];
        this.timer = setTimeout(() => this.deleteTick(phrase), this.TYPE_MS);
      } else {
        this.timer = setTimeout(this.typeTick, this.TYPE_MS);
      }
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // bfcache restore guard: navigating back to a cached page fires
    // `pageshow` with `persisted: true` WITHOUT re-running
    // connectedCallback, so a frozen/mid-animation state (or a `stopped`
    // flag from a previous visit) would otherwise persist untouched. Reset
    // fully and restart clean.
    this.onPageShow = (event) => {
      if (!event.persisted) return;
      clearTimeout(this.timer);
      this.stopped = false;
      this.phraseIndex = 0;
      this.charIndex = 0;
      this.mode = 'type';
      this.liveInput().setAttribute('placeholder', this.basePlaceholder);
      // Codex 28 Oct: back to a page whose search box is still focused or typed
      // in: stay stopped (no focusin fires again, so nothing else would stop it).
      const live = this.liveInput();
      if (live === document.activeElement || live.value) {
        this.stopped = true;
        return;
      }
      this.timer = setTimeout(this.typeTick, 500);
    };
    window.addEventListener('pageshow', this.onPageShow);

    this.timer = setTimeout(this.typeTick, 500);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
    if (this.stopHandler) {
      this.removeEventListener('focusin', this.stopHandler);
      this.removeEventListener('input', this.stopHandler);
    }
  }
}

customElements.define('rockoff-search-typewriter', RockoffSearchTypewriter);
